import { GraphQLClient } from 'graphql-request'
import { createClient } from 'graphql-ws'
import { getBackendGraphqlUrl } from './backendUrl'
import { UserFacingGraphQLError } from '@/errorHandling'
import { emitAuthenticationFailureEvent } from '@/lib/auth/authFailureEvents'
import { fetchWithCloudflarePreclearance } from '@/lib/cloudflarePreclearance'

// Browser: same-origin /api/graphql so the proxy forwards cookies. Server: direct backend URL.
const graphqlUrl =
  typeof window !== 'undefined'
    ? `${window.location.origin}/api/graphql/`
    : getBackendGraphqlUrl()

const httpClient = new GraphQLClient(graphqlUrl, {
  credentials: 'include',
  fetch: (input, init) =>
    fetchWithCloudflarePreclearance(input, init, init?.signal ?? undefined),
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

const adminHttpClient = new GraphQLClient(graphqlUrl, {
  credentials: 'include',
  fetch: fetchWithCloudflarePreclearance,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Admin-Context': 'true',
  },
})

const wsClient = createClient({
  url: graphqlUrl,
})

export function fetchData<TData, TVariables extends Record<string, unknown>>(
  query: string,
  variables?: TVariables,
  headers?: RequestInit['headers'],
  signal?: AbortSignal
) {
  return async (): Promise<TData> => {
    try {
      return await httpClient.request({
        document: query,
        variables,
        requestHeaders: headers,
        signal,
      })
    } catch (error) {
      const normalizedError = new UserFacingGraphQLError(
        error,
        'Something went wrong. Please try again.'
      )
      if (normalizedError.authenticationFailure) {
        emitAuthenticationFailureEvent()
      }
      throw normalizedError
    }
  }
}

export function adminFetchData<
  TData,
  TVariables extends Record<string, unknown>,
>(query: string, variables?: TVariables, headers?: RequestInit['headers']) {
  return async (): Promise<TData> => {
    try {
      return await adminHttpClient.request({
        document: query,
        variables,
        requestHeaders: headers,
      })
    } catch (error) {
      const normalizedError = new UserFacingGraphQLError(
        error,
        'Something went wrong. Please try again.'
      )
      if (normalizedError.authenticationFailure) {
        emitAuthenticationFailureEvent()
      }
      throw normalizedError
    }
  }
}

export function subscribeToData<TData>(
  query: string,
  onNext: (data: TData) => void,
  variables?: Record<string, unknown>
) {
  return wsClient.subscribe(
    { query, variables },
    {
      next: result => onNext(result.data as TData),
      error: error => console.error('Subscription error:', error),
      complete: () => {},
    }
  )
}
